import { useCallback, useRef, useState } from 'react';
import { ServiceRegistry } from '@/services/ServiceRegistry';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s ceiling — background sync should finish well before this.

/**
 * Polls the connect-time background sync status for newly connected accounts (issue #363
 * follow-up: "always fetch transactions after bank connection, in the background, not by the
 * frontend, and show them once fetched"). The bank_account_consents insert trigger dispatches the
 * sync server-side; this hook only watches transaction_sync_state (via getSyncStatus) for each
 * account to reach a terminal state, then calls `onSynced` so the caller can refresh the
 * transaction list. Modeled on useConsentPolling's interval/cleanup pattern.
 */
export function useTransactionSyncPolling() {
  const [isPolling, setIsPolling] = useState(false);
  const pollTokenRef = useRef(0);

  const pollAccounts = useCallback((accountIds: string[], onSynced: () => void) => {
    if (accountIds.length === 0) return;

    const transactionService = ServiceRegistry.getTransactionService();
    const token = ++pollTokenRef.current;
    const pending = new Set(accountIds);
    let attempts = 0;
    setIsPolling(true);

    const tick = async () => {
      if (pollTokenRef.current !== token) return; // superseded by a newer connect

      attempts += 1;

      await Promise.all(
        Array.from(pending).map(async (accountId) => {
          try {
            const { status } = await transactionService.getSyncStatus(accountId);
            if (status === 'completed' || status === 'failed') {
              pending.delete(accountId);
            }
          } catch (err) {
            console.error(`Sync status poll failed for account ${accountId}:`, err);
          }
        }),
      );

      if (pollTokenRef.current !== token) return;

      if (pending.size === 0) {
        setIsPolling(false);
        onSynced();
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        console.warn(
          `Gave up polling sync status for accounts [${Array.from(pending).join(', ')}] after ${MAX_ATTEMPTS} attempts`,
        );
        setIsPolling(false);
        onSynced(); // Refresh anyway — a slow sync will still show up on the next manual refresh.
        return;
      }

      setTimeout(tick, POLL_INTERVAL_MS);
    };

    setTimeout(tick, POLL_INTERVAL_MS);
  }, []);

  return { isPolling, pollAccounts };
}

export default useTransactionSyncPolling;
